<template>
    <div ref="sentinelRef" />
    <Transition
        v-if="isMounted && screenOrientation !== 'landscape'"
        :enterFromClass="$style.fade_enter_from"
        :enterActiveClass="$style.fade_enter_active"
        :leaveActiveClass="$style.fade_leave_active"
        :leaveToClass="$style.fade_leave_to"
        appear
    >
        <div
            v-if="isBannerVisible"
            :class="$style.banner"
        >
            <template v-if="isContainerVisible && isStickyMode">
                <div :class="$style.backdrop" />
                <button
                    :class="$style.close_button"
                    data-interface-target="close-sticky-desktop-banner"
                    @click="onClose"
                >
                    <Icon
                        name="close"
                        :class="$style.close_icon"
                    />
                </button>
            </template>
            <AdContainer
                :type="props.type"
                :class="$style.ad_block_override"
                @update:visible="onAdVisibilityChanged"
            />
        </div>
    </Transition>
</template>

<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { ref } from 'vue'

import AdContainer from '@/components_new/AdContainer.vue'
import Icon from '@/components_new/Icon/Icon.vue'
import type { PageAdType } from '@/modules/adv/types'
import { useLayoutStore } from '@/store/layout-store'
import { useLoggerStore } from '@/store/logger'
import { useIsMounted } from '@/utils/vue-hooks/use-is-mounted'

const props = defineProps<{ type: PageAdType }>()

const loggerStore = useLoggerStore()
const layoutStore = useLayoutStore()

// We avoid using @media (--mobile-landscape) with display:none
// to prevent any potential concerns from Google about hidden ads
const { screenOrientation } = storeToRefs(layoutStore)

const isMounted = useIsMounted()

const isContainerVisible = ref(false)
const isBannerVisible = ref(true)
const isStickyMode = ref(true)

const sentinelRef = ref<HTMLDivElement>()

function onClose() {
    loggerStore.logEvent({
        event: 'custom_event',
        eventName: 'ad_close',
        adPlacement: 'sticky_mobile',
    })
    isBannerVisible.value = false
}

function onAdVisibilityChanged(isVisible: boolean) {
    isContainerVisible.value = isVisible
}

useIntersectionObserver(sentinelRef, (entries) => {
    isStickyMode.value = !entries[0]?.isIntersecting
})
</script>

<style module>
.banner {
    position: sticky;
    bottom: 0;
    margin-left: calc(-1 * var(--global-padding-horizontal) - var(--pg-safe-area-left));
    margin-right: calc(-1 * var(--global-padding-horizontal) - var(--pg-safe-area-left));
}

.close_button {
    position: absolute;
    top: -30px;
    right: 0;
    padding: 6px;
    background: transparent;
    pointer-events: auto;
}

.close_icon {
    width: 24px;
    height: 24px;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(20px);
    border-radius: 4px 4px 0 0;
}

.backdrop {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(40px);
}

.ad_block_override {
    max-height: 100px;
}

.fade_enter_active,
.fade_leave_active {
    transition: all 0.8s ease;
}

.fade_enter_from,
.fade_leave_to {
    transform: translateY(300px);
}
</style>
